import { createRoute } from '@tanstack/react-router';
import { useAuth } from '../auth/AuthProvider';
import { useBillingStatus, useCheckoutSession } from '../api/billing';
import { HttpError } from '../api/client';
import { rootRoute } from './__root';

export const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

function PricingPage() {
  const { isAuthenticated, isLoading: authLoading, signIn } = useAuth();
  const status = useBillingStatus();
  const checkout = useCheckoutSession();

  const trialDays = status.data?.trial_days_remaining ?? 14;
  const trialStarted = status.data?.trial_started_at != null;
  const subTier = status.data?.sub_tier ?? 'free';
  const subActive = subTier !== 'free';

  const startCheckout = (sku: 'desktop-license' | 'cloud-sub') => {
    checkout.mutate(sku, {
      onSuccess: (res) => {
        window.location.assign(res.url);
      },
    });
  };

  // Auth is still resolving on first paint — brief loading state so the
  // page doesn't flicker between "Sign in" and the subscribed card.
  if (authLoading) {
    return (
      <section style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ marginTop: 0 }}>Pricing</h1>
        <p style={{ color: '#666' }}>Loading…</p>
      </section>
    );
  }

  // Logged-out visitors: show the pricing card with a Sign-in CTA. Don't
  // fetch billing status (it 401s and shows an ugly error to visitors
  // browsing pricing before they've made an account).
  if (!isAuthenticated) {
    return (
      <section style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ marginTop: 0 }}>Pricing</h1>
        <p style={{ color: '#666' }}>
          $3/month. 14-day free trial, no charge for 14 days. Cancel anytime.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(320px, 480px)',
            justifyContent: 'center',
            gap: 16,
            marginTop: 16,
          }}
        >
          <PricingCard
            title="AI Assist"
            price="$3"
            period="per month"
            highlight
            features={[
              'Helper + Chrome extension',
              'Local indexing, meeting transcripts, summaries',
              'BYOK — your own Anthropic + OpenAI keys',
              'Cloud sync across devices, end-to-end encrypted',
              'Up to 3 devices per account',
              'Free updates',
              'Cancel anytime',
            ]}
            cta={{
              label: 'Sign in to start free trial',
              onClick: () => signIn(),
            }}
          />
        </div>
        <footer style={{ marginTop: 32, fontSize: 13, color: '#888' }}>
          Card required at checkout, but not charged for 14 days. Cancel anytime
          before then via Manage billing — no charge.
        </footer>
      </section>
    );
  }

  // Don't render the SKU cards until billing status has resolved at least
  // once. Otherwise the page flickers — first showing "Buy desktop license"
  // (the default while data is undefined), then flipping to "Licensed ✓"
  // once the fetch settles ~200ms later. The skeleton is plain text rather
  // than a fancy loader because the wait is short and the page is small.
  if (status.isLoading && !status.data) {
    return (
      <section style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ marginTop: 0 }}>Pricing</h1>
        <p style={{ color: '#666' }}>Loading your subscription state…</p>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ marginTop: 0 }}>Pricing</h1>
      <p style={{ color: '#666' }}>
        $3/month. 14-day free trial, no charge for 14 days. Cancel anytime.
      </p>

      {status.data && !subActive && !trialStarted && (
        <div
          style={{
            background: '#dbeafe',
            border: '1px solid #93c5fd',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '8px 0 20px',
            fontSize: 14,
            color: '#1e3a8a',
          }}
        >
          Your <strong>14-day free trial</strong> starts the moment you pair
          the desktop helper. Install it first, or subscribe below to skip
          the trial.
        </div>
      )}

      {status.data && !subActive && trialStarted && trialDays > 0 && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '8px 0 20px',
            fontSize: 14,
          }}
        >
          You're in the free trial —{' '}
          <strong>
            {trialDays} day{trialDays === 1 ? '' : 's'}
          </strong>{' '}
          remaining.
        </div>
      )}

      {status.data && !subActive && trialStarted && trialDays === 0 && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '8px 0 20px',
            fontSize: 14,
            color: '#991b1b',
          }}
        >
          Your trial has ended. Subscribe below to keep using AI Assist.
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 480px)',
          justifyContent: 'center',
          gap: 16,
          marginTop: 16,
        }}
      >
        <PricingCard
          title="AI Assist"
          price="$3"
          period="per month"
          highlight={!subActive}
          owned={subActive}
          features={[
            'Helper + Chrome extension',
            'Local indexing, meeting transcripts, summaries',
            'BYOK — your own Anthropic + OpenAI keys',
            'Cloud sync across devices, end-to-end encrypted',
            'Up to 3 devices per account',
            'Free updates',
            'Cancel anytime',
          ]}
          cta={
            subActive
              ? { label: 'Subscribed ✓', disabled: true, onClick: () => undefined }
              : {
                  label: checkout.isPending ? 'Loading…' : 'Subscribe — $3/mo',
                  disabled: checkout.isPending,
                  onClick: () => startCheckout('cloud-sub'),
                }
          }
        />
      </div>

      {checkout.error && (
        <p style={{ color: 'crimson', marginTop: 16 }}>{describeError(checkout.error)}</p>
      )}

      <footer style={{ marginTop: 32, fontSize: 13, color: '#888' }}>
        Cancel anytime through the Manage billing button on the Settings page. Refunds
        available within 30 days of first paid month.
      </footer>
    </section>
  );
}

function PricingCard(props: {
  title: string;
  price: string;
  period: string;
  features: string[];
  highlight?: boolean;
  owned?: boolean;
  subnote?: string;
  cta: { label: string; onClick: () => void; disabled?: boolean };
}) {
  return (
    <div
      style={{
        border: `2px solid ${props.highlight ? '#2563eb' : '#e5e5e5'}`,
        borderRadius: 12,
        padding: 24,
        background: props.owned ? '#f0fdf4' : '#fff',
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>{props.title}</h2>
      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 36, fontWeight: 700 }}>{props.price}</span>{' '}
        <span style={{ color: '#666' }}>{props.period}</span>
      </div>
      <ul style={{ paddingLeft: 18, margin: '0 0 20px 0', fontSize: 14, lineHeight: 1.6 }}>
        {props.features.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      {props.subnote && (
        <p style={{ color: '#888', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
          {props.subnote}
        </p>
      )}
      <button
        type="button"
        disabled={props.cta.disabled}
        onClick={props.cta.onClick}
        style={{
          width: '100%',
          padding: '10px 16px',
          background: props.cta.disabled ? '#ccc' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          fontWeight: 600,
          cursor: props.cta.disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {props.cta.label}
      </button>
    </div>
  );
}

function describeError(e: unknown): string {
  if (e instanceof HttpError) {
    return `${e.problem.title} (${e.problem.status})${e.problem.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
